import styled from '@emotion/styled';

import { css } from '@emotion/react';
import {
    boxSizes,
    breakpoints,
    colors,
    colorVariables,
    fontSizes,
    fontWeights,
    gutterSizes,
    lineHeights,
    mediaQuery,
} from '@gumtree/ui-library/src/base/theme';
import { Dialog, Container } from '@gumtree/ui-library';

interface SyiUiExptsProps {
    fullHeight?: boolean;
}

const shouldForwardProp = (p: string) => !['fullHeight'].includes(p);

export const CategoryPageWrapper = styled.div`
    .post-jobs-button {
        margin: 224px 0 24px;

        .label-positive {
            display: none !important;
        }
    }
`;

export const SyiPageContainer = styled(Container, { shouldForwardProp })<SyiUiExptsProps>`
    &.container {
        display: flex;
        flex-direction: column;

        box-sizing: border-box;
        max-width: 768px;
        width: 100%;
        background: white;
        padding: 0 ${gutterSizes.xlarge} ${gutterSizes.xxlarge};
        min-height: ${(props) => (props.fullHeight ? '100vh' : '50vh')};

        ${mediaQuery.until(breakpoints.medium)} {
            padding: 0 ${gutterSizes.large} ${gutterSizes.xlarge};
        }
    }
`;

export const getGenericCss = (theme) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    padding-top: ${gutterSizes.xxlarge};

    * {
        box-sizing: border-box;
    }

    h1,
    h2 {
        font-weight: ${fontWeights.bold};
        letter-spacing: 0.02em;
    }

    h1 {
        font-size: 24px;
    }

    h2 {
        font-size: 17px;
        line-height: 1;
        margin-bottom: 8px;
    }

    input {
        height: ${lineHeights.xxlarge};
        line-height: ${lineHeights.small};
        padding: ${gutterSizes.small} ${gutterSizes.base};
        padding-right: ${gutterSizes.large40};
        border: ${boxSizes.borderWidth} solid ${colorVariables.borderLight};
        border-radius: 4px;
        font-size: ${fontSizes.medium};
        font-weight: ${fontWeights.normal};

        &::placeholder {
            color: ${colors.bark40};
        }

        &:focus {
            outline: none;
            border: ${boxSizes.borderWidth} solid ${colorVariables.borderDark};

            &::placeholder {
                color: ${colors.bark20};
            }
        }

        &[type='button'] {
            cursor: pointer;
        }

        &[type='date'] {
            font-family: inherit;
            appearance: none; // For all browsers (e.g. Microsoft Edge)
            -webkit-appearance: none;
            -moz-appearance: none;
            background: transparent;
            color: ${colors.bark};
            min-width: 100%;
            max-height: 44px;
        }

        &:disabled {
            border: none;
            background: #f1f1f1;
        }

        &.btn-link {
            height: auto;

            &:focus {
                border: none;
            }
        }
    }

    textarea {
        width: 100%;
        line-height: 20px;
        padding: ${gutterSizes.base};
        border: 1px solid ${colorVariables.borderLight};
        font-family: 'Readex Pro', 'sans-serif';
        font-weight: ${fontWeights.normal};
        font-size: 16px;
        border-radius: 4px / 4px;
        resize: none;
        min-height: 100px;

        &:focus {
            outline: none;
            border: ${boxSizes.borderWidth} solid ${colorVariables.borderDark};

            &::placeholder {
                color: ${colors.bark20};
            }
        }
    }

    .scroll-top-ref {
        width: 100%;
        height: 1px;
        background: transparent;
        position: relative;
        display: block;
        visibility: hidden;
    }

    .is-invalid {
        input[type='text'].is-invalid,
        input[type='date'].is-invalid,
        input[type='number'].is-invalid,
        input[type='email'].is-invalid,
        input[type='tel'].is-invalid,
        textarea,
        .mobile-modal-trigger {
            border-color: ${colors.red};
        }
    }

    .is-valid {
        input[type='text'],
        input[type='date'],
        input[type='number'],
        input[type='email'],
        input[type='tel'],
        textarea,
        .mobile-modal-trigger {
            border-color: ${colorVariables.borderLight};
        }
    }

    .icon.icon--clear {
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        cursor: pointer;
    }

    button {
        cursor: pointer;
        background: transparent;
        border: none;
    }

    button.active {
        background-color: ${theme.palette.primary.dark};
    }

    button.active {
        background-color: ${theme.palette.primary.dark};
    }

    .txt-emphasis {
        font-weight: ${fontWeights.bolder};
    }

    .txt-secondary {
        color: #635b67 !important;
    }

    .suggested-category-details {
        padding-right: ${gutterSizes.medium};
        text-align: left;
        align-items: center;
        color: ${colors.black};
    }

    .txt-sub {
        font-size: 14px;
        line-height: 1.4;

        a {
            color: ${theme.palette.action.active};
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .error-messages,
    .form-element-errors {
        color: ${colors.red};
        margin-top: ${gutterSizes.small};
    }

    form:not(.is-invalid-form) .validation-error-message {
        /* Hide the "Almost there..." message */
        display: none;
    }

    .grid-row--50 {
        width: 50%;
        padding: 0 ${gutterSizes.base};

        ${mediaQuery.until(breakpoints.medium)} {
            width: 100%;
        }
    }

    .space-mts-32 {
        margin-top: ${gutterSizes.xxlarge};
    }
    .space-mts-12 {
        margin-top: ${gutterSizes.medium};
    }

    .set-left {
        padding-left: 0;
    }

    .form-label {
        margin-bottom: 6px;
    }

    .hidden {
        display: none;
    }

    .button {
        border-radius: ${boxSizes.borderRadius4};
        transition: background-color 222ms ease-out;
        padding: 0 6px;
        height: ${boxSizes.boxHeight};
        line-height: 40px;
        text-align: center;
        justify-content: center;

        &:disabled {
            border: 1px solid ${theme.palette.disabled.border};
            background: ${theme.palette.disabled.main};
            color: ${theme.palette.disabled.mainContrastText};
            pointer-events: none;
        }
    }

    .btn-primary {
        background: ${theme.palette.primary.main};
        color: ${theme.palette.primary.mainContrastText};
        transition: background-color 222ms ease-out;

        &:hover {
            background: ${theme.palette.primary.dark};
            color: ${theme.palette.primary.darkContrastText};
        }

        &:active {
            background: ${theme.palette.primary.darker};
            color: ${theme.palette.primary.darkerContrastText};
        }
    }

    .btn-secondary {
        background: ${theme.palette.secondary.main};
        color: ${theme.palette.secondary.mainContrastText};
        border: ${boxSizes.borderWidth} solid ${theme.palette.secondary.mainContrastText};

        &:hover {
            background: ${theme.palette.secondary.dark};
            color: ${theme.palette.secondary.darkContrastText};
        }

        &:active {
            background: ${theme.palette.secondary.darker};
            color: ${theme.palette.secondary.darkerContrastText};
        }
    }

    .btn-link {
        border: none;
        background: none;
        color: ${theme.palette.action.active};
        padding: 0;
        font-family: inherit;
        font-weight: ${fontWeights.normal};
        line-height: 2;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .label-urgent-l,
    .label-featured-l,
    .label-spotlight-l,
    .label-bump_up-l,
    .label-positive-l {
        color: #fff;
        display: inline-block;
        text-transform: uppercase;
        min-width: 100px;
        text-align: center;
        border-radius: 20px;
        line-height: 1.6;
        font-size: 13px;
        font-weight: ${fontWeights.bold};
    }

    .checkbox-switch {
        border: 0;
        clip: rect(0 0 0 0);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;

        &:focus-visible + label {
            outline: auto;
        }

        & + label {
            padding: 6px 0 6px 35px;
            min-width: 32px;
            min-height: 32px;
            position: relative;
            cursor: pointer;

            &:before {
                content: '';
                position: absolute;
                display: table-cell;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                background: white;
                width: 14px;
                height: 14px;
                border-radius: 4px;
                border: 2px solid #b7b4b7;
            }
        }

        &:checked {
            & + label {
                &:before {
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    display: inline-block;
                    font-family: gumicon, sans-serif;
                    content: '\\e630';
                    text-align: center;
                    font-size: 80%;
                    font-weight: ${fontWeights.bolder};
                    color: white;
                    background: #52a744;
                    border-color: #52a744;
                    line-height: 14px;
                }
            }
        }

        &:disabled {
            & + label {
                color: rgba(0, 0, 0, 0.2);
                cursor: auto;

                &:before {
                    display: none;
                }

                &:after {
                    content: '\\e908';
                    font-family: gumicon, sans-serif;
                    position: absolute;
                    display: inline-block;
                    left: 0;
                    top: calc(50% - ${gutterSizes.medium});
                    font-size: ${fontSizes.medium};
                    color: ${colors.fgButtonDisabled};
                    width: 16px;
                    height: 16px;
                }
            }
        }
    }

    .radio-switch:checked {
        & + label {
            &:before {
                background: ${colors.green};
                background: radial-gradient(
                    ellipse at center,
                    ${colors.green} 0,
                    ${colors.green} 39%,
                    #fff 44%,
                    #fff 100%
                );
            }
        }
    }

    .hr-dark,
    hr,
    .hr {
        border: 0;
        height: 1px;
        background: #d8d6d9;
        margin: 18px 0;
        width: 100%;
        margin-bottom: 16px;
    }

    .flex-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .flex-column {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    .hint-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .flag-icon {
        background-repeat: no-repeat;
        background-size: 56px 37px;
        width: 56px;
        height: 37px;
        display: block;
        border: solid 1px #d8d6d9;
        margin: 0 auto 3px;

        ${mediaQuery.until(breakpoints.medium)} {
            display: none;
        }
    }

    .form-input-with-button {
        display: flex;
        align-items: center;

        input[type='text'] {
            border-radius: 2px 0 0 2px;
            border-right: none;
        }

        button {
            height: 44px;
            padding: 0 12px;
            border-radius: 0 2px 2px 0;

            &#update_postcode_location {
                min-width: 45px;
            }
        }

        &.round {
            margin: 8px 0;
            gap: 8px;

            input[type='text'] {
                width: 100%;
                border-radius: 4px;
                border-right: 1px solid ${colorVariables.borderLight};

                &#post-ad_postcode {
                    max-width: 200px;
                }
            }

            button {
                border-radius: 4px;
            }
        }
    }

    .features-description-desktop,
    .features-description-mobile,
    .features-description {
        .description-text {
            margin-right: 4px;
        }

        .view-example-btn {
            line-height: normal;
            padding: 0;
            color: ${theme.palette.action.active};
            cursor: pointer;
            border: none;
            background: transparent;
            display: inline-block;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    label[for='post-ad_feature_URGENT_selected'],
    label[for='post-ad_feature_URGENT_TOP_selected'],
    label[for='post-ad_feature_URGENT_BOTTOM_selected'],
    label[for='post-ad_feature_SPOTLIGHT_selected'] {
        color: ${colors.bark80};
    }

    label[for='post-ad_feature_FEATURED_selected'] {
        color: ${colors.bark};
        font-weight: ${fontWeights.lightBold};
    }

    ${mediaQuery.from(breakpoints.large)} {
        .features-description-mobile {
            display: none;
        }
    }

    ${mediaQuery.until(breakpoints.large)} {
        .features-description-desktop {
            display: none;
        }
    }

    .features-row {
        display: flex;

        .form-element {
            width: calc(100% - 200px);
            display: flex;
        }

        .features-row-price {
            width: 200px;
            text-align: right;
            padding: 6px 0;
            justify-content: flex-end;

            .select-wrapper {
                width: auto;
            }

            select {
                font-weight: ${fontWeights.bold};
            }
        }

        ${mediaQuery.until(breakpoints.large)} {
            flex-wrap: wrap;
            justify-content: space-between;

            .features-description-mobile {
                margin-top: 6px;
                width: 100%;
            }

            .form-element {
                width: auto;
            }

            .features-row-price {
                width: auto;
            }
        }
    }

    .select-wrapper {
        width: 100%;
        height: 44px;
        display: inline-block;
        position: relative;

        &-small {
            width: auto;
        }

        &:before,
        &:after {
            content: '';
            display: block;
            position: absolute;
            right: 12px;
            top: 50%;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
        }

        &:before {
            border-bottom: 4px solid black;
            transform: translateY(calc(-100% - 2px));
            border-bottom-color: #635b67;
        }

        &:after {
            border-top: 4px solid black;
            transform: translateY(2px);
            border-top-color: #635b67;
        }

        & > select {
            width: 100%;
            height: 100%;
            line-height: 20px;
            padding: 0 22px 2px 10px;
            background: none;
            color: #3c3241;
            z-index: 1;
            appearance: none;
            cursor: pointer;
            font-family: inherit;
            font-size: 16px;
            font-weight: ${fontWeights.normal};
            border: 1px solid ${colors.branch20};
            border-radius: 4px;

            &.is-invalid {
                border-color: ${colors.red};
            }
        }
    }

    .info-box {
        &.updated-email-info {
            margin: 0 0 ${gutterSizes.large} 0;
        }

        &.price-info-box {
            &.has-border {
                margin-bottom: ${gutterSizes.xxxlarge};

                &:after {
                    bottom: -${gutterSizes.xlarge};
                }
            }
        }
    }

    .textbox-prefix-pound {
        position: relative;

        &:before {
            content: '£';
            position: absolute;
            top: 11px;
            left: 10px;
            z-index: 2;
            pointer-events: none;
        }

        .icon--clear {
            top: 28px;
        }
    }

    &.panel {
        border-bottom: none;
        margin-bottom: 4px;

        .panel-content {
            padding-bottom: 0;
        }
    }

    .textbox-prefix-pound {
        .icon--clear {
            top: 22px;
        }
    }

    .l1-location-nav {
        & > ul {
            display: flex;

            & > li {
                flex-basis: 25%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                color: #635b67;
                padding: 9.6px 8px;

                ${mediaQuery.until(breakpoints.medium)} {
                    display: block;
                }

                .icon.mobile-icon {
                    padding: 0;
                }
            }
        }

        .location-button-content {
            display: flex;
            flex-direction: row;

            ${mediaQuery.from(breakpoints.medium)} {
                flex-direction: column;
            }

            width: 100%;
        }
    }

    .card-layout {
        &.features-row {
            display: flex;
            flex-wrap: wrap;
            border: 1px solid ${colorVariables.borderLight};
            border-radius: 8px;
            padding: ${gutterSizes.large};
            margin-bottom: ${gutterSizes.large};

            .features-description {
                color: ${colors.bark80};
                margin-left: 35px;
                letter-spacing: 0.2px;

                .description-text {
                    margin: ${gutterSizes.medium} 0 6px;
                }

                .recommended-flag {
                    margin: ${gutterSizes.small} 0 -${gutterSizes.small};

                    ${mediaQuery.from(breakpoints.msmall)} {
                        display: none;
                    }

                    @media screen and (max-width: 359px) {
                        // Hard fix for broken UI between 360px to 375px
                        display: none;
                    }
                }
            }

            .checkbox-switch {
                & + label {
                    min-width: auto;
                    min-height: auto;
                    padding: 0 0 0 35px;

                    ${mediaQuery.until(breakpoints.small)} {
                        &:before {
                            top: ${gutterSizes.large};
                        }
                    }
                }
            }

            .form-element {
                align-items: center;

                ${mediaQuery.until(breakpoints.large)} {
                    width: 50%;
                }

                ${mediaQuery.until(breakpoints.small)} {
                    align-items: flex-start;
                }

                ${mediaQuery.until(breakpoints.msmall)} {
                    width: auto;
                }
            }

            .features-row-price {
                padding: 0;

                .price-amount {
                    font-weight: ${fontWeights.lightBold};
                }

                .select-wrapper {
                    height: 32px;
                    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 5%);

                    &:before {
                        display: none;
                    }

                    &:after {
                        border-left: 6px solid transparent;
                        border-right: 6px solid transparent;
                        border-top: 6px solid ${colors.bark80};
                        transform: translateY(-50%);
                        right: ${gutterSizes.large};
                    }

                    select {
                        padding: 0 36px 0 ${gutterSizes.medium};
                        border-color: ${colors.bark40};
                        font-weight: ${fontWeights.lightBold};
                    }

                    ${mediaQuery.until(breakpoints.msmall)} {
                        &:after {
                            right: ${gutterSizes.base};
                        }

                        select {
                            padding: 0 ${gutterSizes.xlarge} 0 ${gutterSizes.base};
                        }
                    }

                    @media screen and (max-width: 359px) {
                        // Hard fix for broken UI between 360px to 375px
                        margin: ${gutterSizes.base} 0 0 35px;
                    }
                }
            }
        }

        .label-urgent-l,
        .label-featured-l,
        .label-spotlight-l,
        .label-bump_up-l,
        .label-positive-l {
            text-transform: none;
            min-width: auto;
            padding: ${gutterSizes.small} ${gutterSizes.medium};
            letter-spacing: 0.5px;
        }

        .label-featured-l {
            line-height: 1.6 !important;
        }

        &.select-all-row {
            .checkbox-switch {
                & + label {
                    font-weight: ${fontWeights.normal};
                }
            }
        }

        .recommended-flag {
            display: flex;
            align-items: center;
            color: ${colors.fgButtonDisabled};
            font-size: ${fontSizes.small};
            font-weight: ${fontWeights.normal};

            .icon--bolt {
                margin-right: ${gutterSizes.small};
                transform: rotate(-10deg);
            }
        }

        .icon-label {
            .recommended-flag {
                ${mediaQuery.until(breakpoints.small)} {
                    margin: ${gutterSizes.base} 0 -${gutterSizes.small};
                }

                ${mediaQuery.until(breakpoints.msmall)} {
                    display: none;
                }

                @media screen and (max-width: 359px) {
                    // Hard fix for broken UI between 360px to 375px
                    display: flex;
                }
            }

            ${mediaQuery.until(breakpoints.small)} {
                flex-direction: column;
                align-items: flex-start;
            }
        }
    }

    div[data-q='listing-cap-banner'] {
        margin-bottom: ${gutterSizes.xxlarge};
    }
`;

export const PanelWrapper = styled.div`
    .panel-content {
        padding-bottom: 8px;
    }

    .panel {
        background: ${colors.white};
        position: relative;
        padding: 0;
        display: block;
        border-radius: ${boxSizes.borderRadius3};
        border: none;
        margin: 0;

        &.has-border {
            margin: 0 0 ${gutterSizes.xlarge};

            &:after {
                content: '';
                position: absolute;
                bottom: 0;
                left: -${gutterSizes.xlarge};
                display: block;
                height: 1px;
                width: calc(100% + ${gutterSizes.xxxxlarge});
                background: ${colorVariables.borderLight};
            }

            .panel-content {
                padding: 0 0 ${gutterSizes.xlarge};
            }
        }

        &.has-border-top {
            padding-top: ${gutterSizes.xlarge};

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: -${gutterSizes.xlarge};
                display: block;
                height: 1px;
                width: calc(100% + ${gutterSizes.xxxxlarge});
                background: ${colorVariables.borderLight};
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            &.has-border,
            &.has-border-top {
                &:after {
                    width: calc(100% + ${gutterSizes.xxlarge});
                    left: -${gutterSizes.large};
                }
            }
        }
    }

    .panel-title {
        margin-bottom: 0;
        padding: ${gutterSizes.base} 0;

        ${mediaQuery.from(breakpoints.medium)} {
            padding: ${gutterSizes.base} 0;
        }
    }

    .is-closed,
    .hide-visually {
        display: none !important;
    }

    .panel-dark {
        background: #3c3241;
        color: #fff;
        border: none;
        position: relative;
        margin: 0 0 18px;
        padding: 0;
        border-radius: 3px;

        .panel-content {
            display: flex;
            justify-content: space-between;

            strong {
                font-weight: 700;
            }
        }
    }

    .panel .panel-validation {
        font-size: 16px;
        padding-right: 4px;
        padding-bottom: 2px;

        //#region frontend/src/common/ui-library/src/icon/icon.scss
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        display: inline-block;
        font-family: gumicon, sans-serif;
        font-size: $font-size--xlarge;
        line-height: 1;
        speak: none;
        vertical-align: middle;
        // avoid reflow when font loads
        min-width: 20px;
        //#endregion

        display: none;
    }
    .panel.is-valid .panel-validation {
        display: inline-block;
        color: #52a744;

        &:before {
            /** icon--circ-check */
            content: '\\e63c';
        }
    }
    .panel.is-invalid .panel-validation {
        display: inline-block;
        color: ${colors.red};

        &:before {
            /** icon--circ-error */
            content: '\\e640';
        }
    }

    .category-panel {
        .flex-row {
            gap: ${gutterSizes.large};
        }

        ${mediaQuery.until(breakpoints.small)} {
            .expandable-container {
                margin-top: 8px;
                .flex-row {
                    flex-direction: column;
                    align-items: normal;

                    button {
                        text-align: left;
                    }
                }
            }
        }
    }

    .pet-advice-panel {
        .syi-info-box-content-toggler button {
            color: ${colors.blue};
            font-weight: ${fontWeights.bold};
        }
    }
`;

export const categoryFormElementCss = css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${gutterSizes.medium};

    .search-field-container {
        position: relative;

        input {
            width: 100%;
            font-weight: ${fontWeights.normal};
        }
    }
`;

export const l1CategoryListCss = css`
    display: flex;
    width: 100%;

    & > li {
        flex-basis: calc(100% / 7);
        border: 1px solid ${colorVariables.borderLight};
        border-left: none;
        color: ${colors.bark80};
        cursor: pointer;
        transition: 0.1s ease;

        &:last-of-type {
            border-right: none;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            min-height: 75px;
            .mobile-icon {
                display: none;
            }
        }

        button {
            color: inherit;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            ${mediaQuery.from(breakpoints.medium)} {
                width: 100%;
                height: 100%;
            }
        }

        .icon {
            line-height: 1.25;
        }

        .icon.mobile-icon:last-of-type {
            margin-left: auto;
        }

        &:hover {
            background: ${colors.lightgrey};
        }

        &.is-selected {
            background: ${colors.lightgrey};
            color: ${colors.bark60};
        }
    }

    ${mediaQuery.until(breakpoints.medium)} {
        flex-direction: column;

        & > li {
            display: flex;
            justify-content: space-between;
            align-items: center;

            button {
                width: 100%;
                flex-direction: row;
                justify-content: flex-start;
                padding: 10px 8px;

                .icon {
                    padding: 0 5px;
                }
            }

            & > div {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        }

        &.has-selected {
            & > li:not(.is-selected) {
                display: none;
            }
        }
    }
`;

export const categoryListsCss = css`
    border-bottom: 1px solid ${colorVariables.borderLight};
    display: flex;

    .category-list {
        flex-basis: 25%;
        height: 310px;
        overflow: auto;

        &::-webkit-scrollbar {
            width: 11px;
        }

        &::-webkit-scrollbar-track {
            display: none;
        }

        &::-webkit-scrollbar-thumb {
            box-shadow: inset 0 0 0 7px rgba(0, 0, 0, 0.35),
                inset 0 0 0 1px rgba(255, 255, 255, 0.9);
            border-radius: 6px;
            border: solid ${boxSizes.borderRadius3} transparent;
            transition: 0.2s ease;
        }

        &:hover {
            &::-webkit-scrollbar-thumb {
                box-shadow: inset 0 0 0 7px rgba(0, 0, 0, 0.5),
                    inset 0 0 0 1px rgba(255, 255, 255, 0.9);
            }
        }

        &:not(:last-of-type) {
            border-right: 1px solid ${colorVariables.borderLight};
        }

        li {
            border-bottom: 1px solid ${colorVariables.borderLight};
        }
    }

    ${mediaQuery.until(breakpoints.medium)} {
        flex-direction: column;
    }
`;

export const categoryListItemCss = css`
    padding: ${gutterSizes.medium10} ${gutterSizes.base};
    color: ${colors.bark};
    line-height: 1.4;
    text-align: left;
    width: 100%;

    ${mediaQuery.from(breakpoints.medium)} {
        padding: ${gutterSizes.medium10} ${gutterSizes.large};
    }

    .category-name {
        color: inherit;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .category-list-control {
        color: ${colors.branch60};

        &.icon--circ-check {
            color: ${colors.green};

            &:not(.is-parent) {
                font-size: 20px;
            }
        }
    }
`;

export const locationActionButtonsCss = (theme) => css`
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
    align-items: center;
    padding: ${gutterSizes.large} ${gutterSizes.base};

    #locationIdBtn {
        width: 25%;
        max-width: calc(100% / 6);

        &.is-disabled {
            border: 1px solid ${theme.palette.disabled.border};
            background: ${theme.palette.disabled.main};
            color: ${theme.palette.disabled.mainContrastText};
            pointer-events: none;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            width: 100%;
            max-width: none;
        }
    }

    ${mediaQuery.until(breakpoints.medium)} {
        flex-wrap: wrap;
        flex-direction: column;
        align-items: flex-start;
        padding: ${gutterSizes.large} 0;

        .btn-link {
            margin-top: 18px;
        }
    }
`;

export const continueActionButtonsCss = (theme) => css`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
    padding: ${gutterSizes.xxlarge} 0 ${gutterSizes.large};
    box-sizing: border-box;

    #continueButton {
        width: 25%;
        max-width: calc(100% / 6);
        font-weight: ${fontWeights.bold};

        &.is-disabled {
            border: 1px solid ${theme.palette.disabled.border};
            background: ${theme.palette.disabled.main};
            color: ${theme.palette.disabled.mainContrastText};
            pointer-events: none;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            width: 100%;
            max-width: none;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            padding: 0 96px;
        }
    }

    .btn-link {
        margin-bottom: ${gutterSizes.xxlarge};
    }
`;

export const suggestionCategoriesCss = css`
    margin-bottom: ${gutterSizes.medium};

    h2 {
        margin: ${gutterSizes.large18} 0 14px;
        font-weight: ${fontWeights.bold};
        line-height: 1.4;
    }

    .syi-categorysuggestion {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: ${boxSizes.borderWidth} solid ${colorVariables.borderLight};
        padding: ${gutterSizes.medium} 0;
        line-height: 1.4;
        transition: 0.1s ease;
        cursor: pointer;

        &:hover {
            background: ${colors.lightgrey};
        }

        width: 100%;
    }

    .txt-sub {
        color: ${colorVariables.textNeutral};
        font-size: ${fontSizes.small14};
        letter-spacing: 0;
        text-align: left;
    }

    & > li {
        border: 1px solid ${colors.newBorder} !important;
        border-radius: ${boxSizes.borderRadius8} !important;
        margin-bottom: ${gutterSizes.base};
        padding: ${gutterSizes.large};
        list-style-type: none;

        input + label:before {
            border: ${boxSizes.borderWidth2} solid ${colors.bark40};
        }

        input:checked + label:before {
            border: ${boxSizes.borderWidth2} solid ${colors.blade};
        }
    }
`;

export const jobsButtonCss = css`
    &.post-jobs-button {
        color: ${colors.blade};
        background: ${colors.blade10};
        border: ${boxSizes.borderWidth} solid ${colors.blade10};
        line-height: 40px;
        width: 100%;
        max-width: 360px;
        text-align: left;
        display: flex;
        align-items: center;
        transition: 0.2s ease;
        cursor: pointer;

        &:hover {
            color: ${colors.bark80};
        }

        .icon--chevron-r {
            margin-left: auto;
        }

        .label-positive {
            color: white;
            background: ${colors.green};
            border-radius: ${boxSizes.borderRadius};
            text-transform: uppercase;
            line-height: initial;
            display: inline-block;
            font-size: ${fontSizes.xsmall};
            padding: 3px ${gutterSizes.small};
        }
    }
`;

export const jobsLightboxCss = css`
    cursor: auto;

    .dialog {
        width: 100%;
        max-width: 660px;

        .button {
            width: auto;

            &.dialog-close {
                width: 44px;
                height: 44px;

                .icon {
                    transition: 0.2s ease;
                }

                &:hover {
                    .icon {
                        color: ${colors.bark};
                    }
                }
            }
        }

        .button + .button {
            margin-left: ${gutterSizes.medium};
        }

        .jobs-popup-button-container {
            display: flex;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            margin: ${gutterSizes.large} 6px;
        }
    }
`;

export const draftLightboxCss = css`
    .dialog {
        width: 100%;
        max-width: 660px;

        .button-container {
            display: flex;
            justify-content: space-between;
            .button {
                width: 250px;
            }
            margin-bottom: ${gutterSizes.medium};
        }

        .button.dialog-close {
            width: 44px;
            height: 44px;

            .icon {
                transition: 0.2s ease;
            }

            &:hover {
                .icon {
                    color: ${colors.bark};
                }
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            margin: ${gutterSizes.large} 6px;
        }
    }
`;

export const featureRowCss = (isServices = false, featureType: 'urgent' | 'spotlight' | 'featured' | 'bump_up' | undefined = undefined) => css`
    .label-urgent-l {
        background: ${isServices ? '#419F08' : colors.red};
        margin-right: ${gutterSizes.medium};
    }
    .label-spotlight-l {
        background-color: ${isServices ? '#EFC636' : '#52a744'};
        margin-right: ${gutterSizes.medium};
    }
    .label-featured-l {
        background-color: #007fb0;
        line-height: 2.8 !important;
        margin-right: ${gutterSizes.medium};
    }
    .label-bump_up-l {
        background-color: rgb(33, 72, 90);
        margin-right: ${gutterSizes.medium};
    }

    &.disabled {
        .feature-name {
            background-color: ${colors.bgButtonDisabled};
        }
        .feature-name,
        .features-description-mobile span,
        .features-description-desktop span {
            color: ${colors.fgButtonDisabled};
        }
    }

    .icon-label {
        display: flex;
        align-items: center;
        ${featureType === 'urgent' && isServices && css`
            width: 130%;
        `}
    }
`;

export const featuresCss = css`
    input:disabled + label {
        opacity: 50%;
    }

    &.panel-content {
        padding-bottom: 12px;

        hr {
            margin: 10px 0;

            ${mediaQuery.until(breakpoints.large)} {
                margin: ${gutterSizes.large} 0;

                &:last-of-type {
                    margin-bottom: 12px;
                }
            }
        }
    }

    .features-panel-title {
        margin-top: 0;
    }

    .select-all-row {
        margin-bottom: 6px;

        .form-element {
            display: flex;
        }
    }
`;

export const AdTitleWrapper = styled.div`
    hr {
        margin-top: 12px;
    }

    .ad-title-form {
        width: 100%;

        input[type='text'] {
            width: 100%;
        }
    }

    .ad-title-skeleton {
        height: 44px;
        padding: 4px 8px;
        border: 1px solid #d8d6d9;
        border-radius: 4px;
        display: flex;
        align-items: center;
    }

    ${mediaQuery.until(breakpoints.medium)} {
        .ad-title-form {
            width: 100%;
        }
    }

    .panel-title {
        display: flex;
        align-items: center;
        white-space: pre-wrap;

        .panel-refresh {
            margin-left: auto;
            display: flex;
            align-items: center;
            cursor: pointer;
        }
        .panel-refresh-text {
            font-weight: ${fontWeights.normal};
            margin-left: ${gutterSizes.small};
        }
    }
`;

// export const locationCss = css`
//     .panel-title .panel-summary {
//         float: right;
//         color: ${colors.bark80};
//         font-size: 14px;
//         font-weight: 400;
//         @media (min-width: ${breakpoints.medium}) {
//             display: none;
//         }
//     }

//     .select-location-action {
//         display: flex;
//         flex-wrap: wrap;
//         margin: 0 0 6px;

//         .btn-link {
//             line-height: 1.4;
//         }
//     }

//     .txt-secondary {
//         margin: 12px 0;
//     }

//     .h2 {
//         font-size: 18px;
//         font-weight: 700;
//         color: black;
//         padding-bottom: 12px;
//         display: block;
//         margin: 0;
//     }

//     .form-element {
//         margin-bottom: 6px;
//         padding-bottom: 2px;

//         button {
//             font-size: 16px;
//             float: right;
//         }
//     }

//     #location-display {
//         font-size: ${fontSizes.base};

//         & + .txt-link {
//             line-height: normal;
//         }
//     }

//     .show-map-action {
//         border-top: 1px solid ${colors.bark20};
//         padding-top: 2px;
//         padding-bottom: 0;

//         & > div {
//             display: flex;
//             align-items: center;
//         }

//         hr {
//             display: none;
//         }
//     }
// `;

export const LocationPopUp = styled(Dialog)`
    &.location-popup {
        padding: ${gutterSizes.xxlarge};

        .overlay {
            background: rgba(0, 0, 0, 0.8);
        }

        &.is-popup {
            position: fixed;
            left: 0;
            bottom: 0;
            margin: 0;
            padding: ${gutterSizes.xlarge} ${gutterSizes.large};
            border-radius: 0;
            z-index: 1005;

            .h2 {
                font-size: 17px;
                margin-bottom: 8px;
                display: inline-block;
            }
        }

        .h2 {
            display: inline-block;
            margin-bottom: 8px;
        }

        p {
            margin: 0;
        }

        .location-pin-icon {
            width: 40px;
            height: 40px;
            background: ${colors.branch20};
            border-radius: 50%;
            margin-bottom: 8px;
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            align-items: center;

            .icon {
                font-size: 18px;
            }
        }

        .select-location-action {
            & > p {
                margin: ${gutterSizes.large} 0 0;
            }
        }
    }

    .dialog {
        max-width: 768px;
        width: 100%;
        border: none;
        border-radius: 8px;
        box-shadow: 0 1px 24px 0 rgb(0 0 0 / 20%);
    }
`;

export const DescriptionPanelWrapper = styled.div`
    .panel-content > div {
        display: flex;
        flex-direction: column;

        .form-element,
        .description-guide {
            width: 100%;
            color: #8a848d;

            p {
                margin: 0;
            }
        }

        .description-guide {
            display: flex;
            margin-top: ${gutterSizes.small};

            .guide-text {
                flex-grow: 1;

                .form-element-errors {
                    margin-top: 0;
                }
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            flex-direction: column;
            gap: 0;

            .form-element,
            .description-guide {
                width: 100%;
            }
        }
    }

    .panel-title {
        display: flex;
        align-items: center;
        white-space: pre-wrap;

        .panel-refresh {
            margin-left: auto;
            display: flex;
            align-items: center;
            cursor: pointer;
        }
        .panel-refresh-text {
            font-weight: ${fontWeights.normal};
            margin-left: ${gutterSizes.small};
        }
    }

    .panel-description-info-block {
        background-color: #f6f6f7;
        div {
            display: flex;
            align-items: flex-start;
            gap: ${gutterSizes.base};

            .panel-description-info-block-text {
                margin-top: ${gutterSizes.xsmall};
                font-size: ${fontSizes.small};
            }
        }
    }

    .description-skeleton {
        min-height: 100px;
        border: 1px solid #d8d6d9;
        padding: 8px;
        border-radius: 4px / 4px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
`;

export const deliveryCss = css`
    padding: ${gutterSizes.large} 0 !important;

    .panel-title {
        .panel-title-label {
            width: 45px;
            height: 20px;
            text-align: center;
            margin-bottom: ${gutterSizes.large};
            background-color: ${colors.gree20};
            font-weight: ${fontWeights.bold};
            font-size: ${fontSizes.small};
            line-height: ${lineHeights.small};
        }
    }

    .panel-title-content {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .switch-box {
            display: flex;
            align-items: center;
            .switch-container {
                position: relative;
                display: inline-block;
                width: 48px;
                height: 24px;

                input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }

                .switch {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: ${colors.branch60};
                    transition: background-color 0.4s;
                    border-radius: 24px;

                    &.isChecked {
                        background-color: ${colors.blade};

                        &::before {
                            transform: translateX(16px);
                            left: 12px;
                        }
                    }

                    &.disabled {
                        cursor: not-allowed;
                    }

                    &::before {
                        position: absolute;
                        content: '';
                        height: 16px;
                        width: 16px;
                        left: 4px;
                        bottom: 4px;
                        background-color: white;
                        transition: transform 0.4s;
                        border-radius: 50%;
                    }
                }
            }
        }
    }
    .panel-content {
        .txt-btn {
            text-decoration: underline;
            color: ${colors.navy};
            font-weight: ${fontWeights.bold};
            line-height: 24px;
            padding: 0;
            margin-top: 8px;
        }
        .delivery-text {
            margin-top: ${gutterSizes.medium};
            p {
                margin: 0 0 20px 0;
                font-weight: ${fontWeights.normal};
                font-size: ${fontSizes.base};
                line-height: ${lineHeights.base};
            }
            .txt-btn {
                margin-top: -2px;
            }
        }
        .select-container {
            max-width: 343px;
            margin-top: ${gutterSizes.large};
        }

        .select-label {
            font-weight: ${fontWeights.normal};
            font-size: ${fontSizes.base};
            line-height: ${lineHeights.base};
            margin-bottom: ${gutterSizes.small};
        }
    }
`;

export const SwitchDescription = styled.p`
    margin: 0;
    padding-right: ${gutterSizes.medium};
    font-size: 15px;
`;

export const loadingCss = css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    .loading-spinner {
        width: 32px;
        height: 32px;
        animation: spin 1s linear infinite;
        display: block;
    }
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export const selectCss = css`
    .custom-select-container {
        position: relative;
        width: 100%;
    }

    .disabled {
        color: #fff;
        cursor: not-allowed;
    }

    .custom-select-header {
        padding: 10px 12px;
        border: 1px solid ${colors.bark40};
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .placeholder {
            font-weight: ${fontWeights.normal};
            font-size: ${fontSizes.base};
            line-height: ${lineHeights.base};
            color: ${colors.bark40};
        }
    }

    .custom-select-options {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        border: 1px solid #ddd;
        border-radius: 6px;
        background-color: white;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        z-index: 100;
        max-height: 300px;
        overflow-y: auto;
        display: none;
    }

    .custom-select-container.disable .custom-select-header {
        cursor: not-allowed;
    }

    .custom-select-container.openeq .custom-select-options {
        display: block;
    }

    .custom-option {
        padding: ${gutterSizes.large};
        ${gutterSizes.large};
        ${gutterSizes.medium};
        ${gutterSizes.large};
        cursor: pointer;
        transition: background-color 0.2s;
    }

    .custom-option:hover {
        background-color: ${colors.branch20};
    }

    .custom-option.selected {
        background-color: ${colors.branch20};
    }

    .option-title {
        font-weight: ${fontWeights.bold};
        font-size: ${fontSizes.base};
        line-height: ${lineHeights.base};
        color: #000;
    }

    .option-text {
        font-weight: ${fontWeights.normal};
        font-size: ${fontSizes.base};
        line-height: ${lineHeights.base};
        color: #000;
    }
`;

export const websiteLinkCss = css`
    .form-element {
        display: flex;

        input {
            width: 100%;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            display: inline;
            padding-right: 4px;
        }
    }

    .website-link-input-wrapper {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
        margin-top: 2px;

        &.hide-input {
            display: none;
        }

        &.reveal-input {
            display: flex;
        }

        & > div {
            width: calc(50% - 8px);

            .wrapper {
                width: 100%;
            }

            .form-element-errors {
                width: 100%;
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            flex-direction: column;
            gap: 6px;

            & > div {
                width: 100%;

                .wrapper {
                    margin-bottom: 6px;
                }
            }
        }
    }
`;

export const ContactDetailWrapperCss = (theme) => css`
    .contact-detail-fill-row {
        display: flex;
        align-items: center;
        gap: ${gutterSizes.base};
        margin-bottom: ${gutterSizes.large};

        &.no-checkbox {
            margin: ${gutterSizes.medium} 0 6px;
        }

        & > .form-element {
            &:first-of-type {
                flex-basis: 150px;
            }

            &:last-of-type {
                flex-basis: calc(50% - 150px);

                input {
                    width: 100%;
                }
            }
        }

        .icon--clear {
            top: 22px;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            & > .form-element {
                &:first-of-type {
                    flex-basis: unset;
                    width: 140px;
                }

                &:last-of-type {
                    flex-basis: unset;
                    width: 100%;
                }
            }
        }
    }

    .phone-row {
        .contact-detail-fill-row {
            margin-bottom: 0;
        }
    }

    .panel-content {
        & > p {
            &:first-of-type {
                margin-top: 0;
            }
        }

        a {
            color: ${theme.palette.action.active};

            &.btn-primary {
                color: ${theme.palette.primary.mainContrastText};
            }
        }
    }

    .contact-details-pro {
        .contact-detail-fill-row {
            margin-bottom: 6px;

            &.no-checkbox {
                margin-top: ${gutterSizes.medium};
            }
        }
    }

    input {
        &::-webkit-calendar-picker-indicator {
            margin-right: 22px;
        }

        &.is-empty {
            &::-webkit-calendar-picker-indicator {
                margin-right: 0;
            }
        }
    }

    .contact-info {
        margin-top: ${gutterSizes.base};
        color: ${colors.bark80};

        strong {
            color: ${colors.bark};
        }

        ${mediaQuery.until(breakpoints.small)} {
            .grid-col-12 {
                div {
                    display: inline;

                    &:first-of-type {
                        padding-right: ${gutterSizes.small};
                    }
                }
            }
        }
    }

    .panel-title {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .btn-link {
            font-size: 16px;
        }
    }

    .contact-details-preview {
        & > li {
            display: flex;
            margin-bottom: ${gutterSizes.base};

            .contact-label {
                padding-right: ${gutterSizes.base};
            }

            .contact-values {
                font-weight: 600;
                word-break: break-all;
            }
        }
    }

    .preview-wrapper {
        display: flex;

        ${mediaQuery.until(breakpoints.medium)} {
            flex-direction: column;

            .grid-row--50 {
                padding: 0;
            }
        }

        hr {
            margin: ${gutterSizes.base} 0;
        }
    }

    .contact-details-edit {
        p {
            margin: 8px 0;

            &.txt-secondary {
                display: none; // 🚧 hide link for now until the functionality on My Details is fixed
            }

            .btn-link {
                line-height: inherit;
            }
        }

        span[data-q='contact-email'] {
            word-break: break-all;
        }
    }

    .location-edit {
        .location-name {
            h4 {
                font-size: ${fontSizes.base};
                font-weight: ${fontWeights.normal};
                margin-bottom: 14px;
            }

            .btn-link {
                margin-bottom: 4px;
            }

            #location-display {
                font-weight: ${fontWeights.bold};
                font-size: ${fontSizes.large};
            }

            button.border-n {
                display: block;
            }
        }
    }

    .location-browser-lightbox {
        .dialog {
            border-radius: 8px;
            width: 100%;
            max-width: 775px;
            padding: ${gutterSizes.xxlarge};
        }
    }

    &.new-contact-details {
        input {
            border-color: ${colors.bark40};

            &:focus {
                outline: none;
                border: ${boxSizes.borderWidth} solid ${colorVariables.borderDark};

                &::placeholder {
                    color: ${colors.bark20};
                }
            }

            &:disabled {
                border: 1px solid ${colors.newLigthGrey};
                background-color: ${colors.lightgrey};
                color: ${colors.bark60};
            }
        }

        .select-wrapper {
            &:before {
                display: none;
            }

            &:after {
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-top: 8px solid ${colors.bark};
                transform: translateY(-3px);
            }

            select {
                border-color: ${colors.bark40};
                outline: none;
            }
        }

        .is-invalid {
            input[type='text'].is-invalid,
            input[type='number'].is-invalid,
            input[type='email'].is-invalid,
            input[type='tel'].is-invalid,
            .mobile-modal-trigger {
                border-color: ${colors.red};
            }
        }

        .preview-wrapper {
            display: block;

            .form-element {
                margin-bottom: ${gutterSizes.medium};
            }

            .contact-values {
                margin-left: ${gutterSizes.base};

                & > a {
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                    display: inline-block;
                    width: calc(100% - 250px);
                    vertical-align: middle;
                    margin-bottom: ${gutterSizes.small};
                }
            }

            .checkbox-switch {
                & + label {
                    display: inline-block;
                    min-width: 95px;
                }
            }
        }

        .contact-email-hint-text {
            margin: 0 0 0 35px;

            ${mediaQuery.until(breakpoints.small)} {
                font-size: ${fontSizes.small};
            }
        }

        .txt-btn {
            text-decoration: underline;
            color: ${colors.navy};
            font-weight: ${fontWeights.bold};
        }

        .contact-detail-fill-row {
            display: block;
            width: 100%;
            max-width: 500px;

            & > .form-element {
                &:first-of-type,
                &:last-of-type {
                    flex-basis: unset;

                    input {
                        &:not(:disabled) {
                            color: ${colors.bark};
                        }
                    }
                }
            }
        }

        .info-box.info-box {
            p {
                margin: 0;
            }
        }
    }
`;

export const PricePanelWrapperStyle = (theme) => css`
    .grid-row--50 {
        input {
            width: 100%;
            margin: 0 0 12px;
            padding-left: 22px;

            &::-webkit-inner-spin-button {
                display: none;
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            &.set-left {
                padding-right: 0;
            }
        }
    }

    .textbox-prefix-pound {
        position: relative;

        &:before {
            content: '£';
            position: absolute;
            top: 11px;
            left: 10px;
            z-index: 2;
            pointer-events: none;
        }

        .icon--clear {
            top: 28px;
        }

        .hint-text {
            margin: 4px 0 12px 0;
        }
    }

    &.panel {
        border-bottom: none;
        margin-bottom: 4px;

        .panel-content {
            padding-bottom: 0;
        }
    }

    .textbox-prefix-pound {
        .icon--clear {
            top: 22px;
        }
    }

    .vat-radios-row {
        display: flex;
        flex-wrap: wrap;
        width: 50%;
        margin-top: ${gutterSizes.base};

        & > .form-element {
            flex-basis: calc(100% / 3);
        }

        & > .form-element-errors {
            flex-basis: 100%;
        }

        .radio-switch {
            display: none;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            width: 100%;
        }
    }

    button.button-link {
        padding: 0;
        margin: 0;
        color: ${theme.palette.action.active};

        &:hover {
            text-decoration: underline;
        }
    }
`;

export const ConfirmationWrapper = styled.div`
    margin-top: ${gutterSizes.base};

    .grid-row {
        .wrapper {
            display: flex;
            flex-direction: row-reverse;
            gap: 16px;

            button {
                flex-basis: 50%;
                height: 44px;
                min-height: 44px;
                line-height: 40px;
                border-radius: 4px;

                display: flex;
                align-items: center;
                justify-content: center;
                gap: ${gutterSizes.small};
            }

            & > div {
                flex-basis: 50%;
                margin: 0 0 6px;
                color: #635b67;

                ${mediaQuery.until(breakpoints.medium)} {
                    margin-top: ${gutterSizes.large};
                }
            }

            ${mediaQuery.until(breakpoints.medium)} {
                flex-direction: column;
                gap: 0;

                button {
                    flex-basis: 100%;
                }
            }
        }
        .no-reverse {
            flex-direction: row;
            font-weight: ${fontWeights.bold};
        }
    }

    .error-messages {
        text-align: right;
    }
`;

export const carListingOptionsCss = css`
    .panel-title {
        line-height: 1.3;
    }

    .radio-switch {
        display: none;

        & + label {
            margin-bottom: 0;
            font-weight: 500;
        }

        &.is-disabled + label {
            opacity: 0.4;
            cursor: default;
            pointer-events: none;

            &:before {
                border: 2px solid ${colors.branch10};
                opacity: 0.4;
                cursor: default;
                pointer-events: none;
            }

            & + .label-description {
                opacity: 0.4;
            }
        }
    }

    .radio-button-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-top: 8px;
    }

    .label-description {
        color: ${colors.bark60};
        padding-left: 40px;
        margin: ${gutterSizes.small} 0 0;
        font-size: 14px;
    }

    .dealer-listing-form-element {
        padding-top: ${gutterSizes.large};
    }
`;

export const ImagesPanel = styled.div<{ theme: any }>`
    &.panel.has-border-top {
        padding-top: 28px;

        &:before {
            top: 4px;
        }
    }

    .info-box {
        margin: ${gutterSizes.large} 0;
    }

    .add-images {
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%238E969C' stroke-width='2' stroke-dasharray='4%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
        min-height: 220px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        padding-bottom: ${gutterSizes.large};
        position: relative;
        transition: 0.2s ease;

        &:after {
            content: '';
            width: 100%;
            height: 100%;
            background: transparent;
            position: absolute;
            top: 0;
            left: 0;
            z-index: -1;
            transition: 0.2s ease;
        }

        .img-wrap {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 0;
            padding-bottom: 100%;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            cursor: pointer;

            div.spinner {
                position: absolute;
                background: #3c324166;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                top: 0;
                left: 0;

                svg {
                    margin-left: 0;
                }
            }

            a {
                position: absolute;
                right: 6px;
                top: 6px;
                background: rgba(255, 255, 255, 0.8);
                border-radius: 50%;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: 0.2s ease;
                z-index: 5;

                &:hover {
                    text-decoration: none;
                    background: rgba(255, 255, 255, 1);
                }
            }

            .icon--clear {
                font-size: ${fontSizes.smaller};
                position: relative;
                transform: none;
                color: ${colors.messageTextColor};
                top: auto;
                right: auto;
                min-width: auto;
            }
        }

        .add-image {
            display: flex;
            align-items: center;
            justify-content: center;
            grid-column: 2 / span 2;
            width: auto;
            background-color: transparent;
            border: none;
            flex-direction: column;
            position: relative;
            z-index: 0;

            & > label {
                width: 50%;
                height: 100%;
                cursor: pointer;

                ${mediaQuery.until(breakpoints.medium)} {
                    width: 100%;
                }
            }

            .center-add-image {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: ${colors.surface};
                border: 1px solid ${colors.bark20};
                border-radius: 8px;
                position: relative;
                transition: 0.2s ease;

                &:after {
                    content: '';
                    display: block;
                    padding-top: 100%;
                }

                &:hover {
                    background: ${colors.surfaceHover};
                }

                &.is-reordering {
                    &:hover {
                        background: ${colors.surface};
                    }
                }

                ${mediaQuery.until(breakpoints.medium)} {
                    width: 100%;
                    min-height: auto;
                }
            }

            a.link {
                font-weight: 400;
                font-size: ${fontSizes.base};
                display: flex;
                align-items: center;
                flex-direction: column;

                .add-photos-label {
                    width: 70px;
                    height: 70px;
                    background-size: contain;
                    background-position: center;
                    background-repeat: no-repeat;

                    ${mediaQuery.until(breakpoints.small)} {
                        width: 55px;
                        height: 55px;
                    }
                }

                &:hover {
                    text-decoration: none;
                }
            }

            .icon {
                margin-right: 4px;
            }
        }

        .form-element-errors {
            text-align: center;
        }

        &.active {
            background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%231B73E8' stroke-width='2' stroke-dasharray='4%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");

            &:after {
                background: rgba(184, 212, 248, 0.1);
            }

            .center-add-image {
                background: rgba(184, 212, 248, 0.1);
                border-color: #b8d4f8;

                &.is-reordering {
                    &:hover {
                        background: ${colors.surface};
                    }
                }
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            background-image: none;
            min-height: auto;
            padding-bottom: 0;
        }
    }

    ul.images {
        display: grid;
        grid-gap: ${gutterSizes.base};
        grid-template-columns: 1fr 1fr 1fr 1fr;
        padding: ${gutterSizes.large} ${gutterSizes.large} 0;
        width: 100%;

        li {
            overflow: hidden;
            opacity: 1;
            transition: opacity 300ms;
            position: relative;
            z-index: 0;

            &.fade {
                opacity: 0;
            }

            &.add-image:not(:first-of-type) {
                grid-column: span 1;

                & > label {
                    width: 100%;
                }
            }

            .first.img-wrap {
                /* Cover Badge */
                &:before {
                    content: 'Cover';
                    display: block;
                    position: absolute;
                    top: 0;
                    left: 0;
                    font-size: ${fontSizes.small};
                    padding: 0px ${gutterSizes.small};
                    font-weight: 600;
                    background-color: ${colors.newBrand};
                    color: ${colors.navy};
                }
            }

            ${mediaQuery.until(breakpoints.medium)} {
                &.add-image {
                    grid-column: 2 / span 1;
                }
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            grid-template-columns: 1fr 1fr 1fr;
            padding: 0;
        }
    }

    .info-wrapper {
        background: none;
        border: none;
        grid-column: 1 / span 4;

        .info {
            background-color: ${colors.surface};
            border-radius: 8px;
            padding: ${gutterSizes.large};
            margin-top: ${gutterSizes.large};

            p {
                color: ${colors.newDarkGrey};
            }
        }

        .images-info-block {
            p {
                display: flex;

                span {
                    padding-right: ${gutterSizes.base};
                }
            }
        }

        li {
            background: none;
            border: none;
        }
    }

    .images-hint-text {
        margin: ${gutterSizes.base} 0 0;
        position: relative;
        z-index: 0;
        transition: 0.2s ease;

        .image-counter-text {
            position: relative;

            &:after {
                content: '';
                width: 4px;
                height: 4px;
                border-radius: 50%;
                margin: 0 ${gutterSizes.base};
                background: ${colors.newLigthGrey};
                position: relative;
                display: inline-block;
                vertical-align: middle;
            }
        }

        ${mediaQuery.until(breakpoints.medium)} {
            margin-bottom: 0;
        }
    }

    .info {
        ${mediaQuery.until(breakpoints.medium)} {
            grid-column: span 3;
        }
    }

    .add-link {
        display: block;
        grid-row: 2 / 2;
        margin-top: ${gutterSizes.base};

        label {
            position: relative;
            width: 100%;
            display: block;
            height: 38px;
            font-weight: normal;
            display: flex;
            align-items: center;
            gap: 8px;

            .icon {
                font-size: 15px;
                cursor: pointer;
            }

            &:hover {
                text-decoration: none;

                .btn-link-text {
                    text-decoration: underline;
                }
            }
        }

        div.form-element {
            width: 40%;

            &.textbox-prefix-youtube {
                width: 100%;
            }

            input {
                width: 100%;
            }
        }
    }

    .textbox-prefix-youtube {
        position: relative;
        margin-top: ${gutterSizes.small};

        input#post-ad_youtube {
            width: 100%;

            ${mediaQuery.until(breakpoints.medium)} {
                width: 100%;
            }
        }
    }

    .info {
        grid-row: 1 / 2;

        p {
            color: ${colors.bark};
            font-size: ${fontSizes.base};
            margin: 0;
        }
    }

    &.is-invalid {
        .add-images {
            ${(props) => `
                background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='${encodeURIComponent(
                    props.theme.palette.error.text
                )}' stroke-width='2' stroke-dasharray='4%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
            `}

            .add-image {
                .center-add-image {
                    border-color: ${(props) => props.theme.palette.error.text};
                }
            }

            ${mediaQuery.until(breakpoints.medium)} {
                background-image: none;
            }
        }
    }
`;

export const attributeCss = css`
    .grid-row {
        margin-top: ${gutterSizes.small};

        input,
        select {
            width: 100%;
        }
    }
`;

export const sellerTypeCss = (theme) => css`
    &.panel.has-border-top {
        padding-top: 28px;

        &:before {
            top: 4px;
        }
    }

    .seller-types-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${gutterSizes.large};

        ${mediaQuery.until(breakpoints.medium)} {
            grid-template-columns: 1fr;
            gap: 0;
        }

        &.grid-row--full {
            display: flex;
            flex-direction: column;
            width: 100%;

            .radio-buttons-wrapper {
                flex-direction: column;
                gap: ${gutterSizes.large};

                & > div {
                    width: 100%;

                    p {
                        margin: 0 0 ${gutterSizes.large} 38px;

                        .btn-link {
                            line-height: normal;
                        }
                    }
                }
            }
        }
    }

    .radio-buttons-wrapper {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin-top: ${gutterSizes.small};
        width: 100%;

        input[type='radio'] {
            display: none;
        }

        .radio-switch:disabled + label:before {
            border: 2px solid #f1f1f1;
            opacity: 0.4;
            cursor: default;
        }

        & > div {
            width: calc(100% / 3);

            &.grid-col-12 {
                width: 100%;
            }

            ${mediaQuery.until(breakpoints.small)} {
                width: 50%;
            }
        }

        & + div > p {
            margin: 0 0 ${gutterSizes.base};
        }
    }

    .txt-orphan + span > a {
        text-decoration: none;
        color: ${theme.palette.action.active};
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .mfp-hide {
        max-width: 100%;
        padding: 40px;
        margin: ${gutterSizes.base};
        letter-spacing: 0;
        border-radius: 8px;
        position: absolute;

        p.border-b {
            padding-bottom: ${gutterSizes.large};
            border-bottom: 1px solid ${colors.bark10};
            margin-top: 0;
        }

        .btn-primary {
            height: 44px;
            display: inline-block;
            padding: 10px 16px;
            border-radius: 4px;
        }

        &.is-popup {
            position: fixed;
            left: 0;
            bottom: 0;
            width: 100%;
            max-width: 100%;
            margin: 0;
            border-radius: 0;
            padding: 40px ${gutterSizes.large};
            box-shadow: none;
            z-index: 1000;

            & > .dialog-content {
                padding: 0;
                flex-direction: column;
                align-items: center;
                gap: 8px;

                & > div {
                    flex-basis: 100%;
                    width: 100%;

                    &:not(.text-wrapper) {
                        text-align: center;
                    }
                }
            }
        }
    }

    .business-membership-row {
        display: flex;
        align-items: baseline;
        gap: ${gutterSizes.base};

        .txt-link {
            display: flex;
            align-items: center;
            gap: ${gutterSizes.small};
        }

        p {
            margin: 0 0 ${gutterSizes.large20};
        }
    }

    .listing-cap-modal {
        max-width: 600px;

        h4 {
            margin-bottom: 0;
        }

        p {
            margin: ${gutterSizes.large} 0 0 0;
        }

        .btn-link {
            display: flex;
            align-items: center;
            gap: ${gutterSizes.small};
            margin-top: ${gutterSizes.base};
        }

        .btn-primary {
            display: block;
            margin-top: ${gutterSizes.xlarge};
            margin-left: auto;
        }
    }
`;

export const legalCss = (theme) => css`
    .mfp-hide {
        margin: ${gutterSizes.base};
        letter-spacing: 0;
        max-width: 660px;
        border-radius: 8px;
        padding: 40px;

        ul + p {
            margin-top: 0;
        }

        h2 + p > strong {
            font-weight: ${fontWeights.bold};
        }

        label[for='legal-popup-check'] {
            text-align: left;
        }

        &.is-popup {
            position: fixed;
            left: 0;
            bottom: 0;
            width: 100%;
            margin: 0;
            border-radius: 0;
            padding: 40px ${gutterSizes.large};
            box-shadow: none;
            z-index: 1000;

            & > .dialog-content {
                padding: 0;
                flex-direction: column;
                align-items: center;
                gap: 8px;

                & > div {
                    flex-basis: 100%;
                    width: 100%;

                    &:not(.text-wrapper) {
                        text-align: center;
                    }
                }
            }
        }
    }

    .checkbox-switch {
        & + label {
            display: inline-block;

            a {
                color: ${theme.palette.action.active};

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
`;

export const additionalFeaturesCss = css`
    .desktop-feature-preview {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: ${gutterSizes.base};

        .form-element {
            display: flex;
            align-items: center;

            &:nth-of-type(n + 7) {
                display: none;
            }
        }
    }

    .desktop-preview-summary {
        margin-bottom: ${gutterSizes.large};

        & + .btn-link {
            line-height: 1;
            margin-bottom: ${gutterSizes.base};
        }
    }

    .dialog {
        width: 100%;
        max-width: 780px;
        max-height: 90%;
        padding: 0;
        border: none;
        border-radius: 0;

        .clear-features {
            text-align: left;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            display: flex;
            flex-wrap: wrap;
            border-radius: 8px;

            .dialog-title {
                order: 1;
                width: calc(100% - 44px);
            }

            .button {
                order: 2;
                height: 65px;
                background: white;
                position: sticky;
                border-radius: 0;
                top: 0;
                right: 0;
                z-index: 6;
                box-shadow: 15px 0 10px 5px rgba(0, 0, 0, 0.05);
            }

            .dialog-content {
                order: 3;
                width: 100%;
            }

            .icon--clear {
                right: 16px;
                font-weight: ${fontWeights.normal};
            }
        }

        .close-features {
            position: sticky;
            z-index: 1;
            height: 0;
            top: calc(100% - (2 * 24px) - 30px);

            & > div {
                display: flex;
                justify-content: flex-end;
                width: 100%;
                background: #fff;
                border-top: 1px solid ${colors.bark20};
                height: calc((2 * 24px) + 30px);
                padding: ${gutterSizes.large} ${gutterSizes.xlarge};
            }
        }

        .dialog-title {
            position: sticky;
            left: 0;
            top: 0;
            background: white;
            padding: ${gutterSizes.xlarge};
            z-index: 5;
            box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);

            ${mediaQuery.until(breakpoints.medium)} {
                display: flex;
                background: #fff;
                color: ${colors.bark};
                padding: ${gutterSizes.xlarge} ${gutterSizes.large};
                position: sticky;
                top: 0;
                left: 0;
                width: 100%;
                z-index: 5;
                box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);
            }
        }

        .steps-counter {
            color: ${colors.bark60};
            font-size: 14px;
            position: absolute;
            top: 49px;
            right: ${gutterSizes.xlarge};
            z-index: 5;

            ${mediaQuery.until(breakpoints.medium)} {
                right: ${gutterSizes.large};
            }
        }

        .btn-primary,
        .btn-secondary {
            width: 176px;
            border-radius: ${boxSizes.borderRadius4};
            line-height: 40px;
            height: 44px;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            padding-top: 0;
            background-color: white;
            max-width: 600px;
            max-height: 100%;
            margin: 0;

            .close-features {
                top: calc(100% - (2 * 24px) - 20px);

                & > div {
                    height: auto;
                    border: 0;
                    background: white;
                    box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);
                    padding: 12px;
                }

                &.taller {
                    top: calc(100% - (2 * 24px) - 92px);
                }
            }

            .btn-primary,
            .btn-secondary {
                width: 100%;
            }

            .btn-secondary {
                margin-top: ${gutterSizes.medium};
            }

            .icon--clear {
                font-weight: ${fontWeights.normal};
                color: ${colors.bark60};
                position: fixed;
                top: ${gutterSizes.xxlarge};
                right: ${gutterSizes.large};
            }
        }
    }

    .modal-feature-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        background-color: white;
        padding: ${gutterSizes.base};
        grid-gap: ${gutterSizes.medium} 0;
        padding: 16px 24px 100px;

        ${mediaQuery.until(breakpoints.medium)} {
            padding: 0 8px 50px;
            margin: ${gutterSizes.base};
            grid-template-columns: 1fr;
        }

        .question-description {
            margin: 0 0 ${gutterSizes.base};

            ${mediaQuery.from(breakpoints.medium)} {
                grid-column: 1 / 3;
            }
        }

        .form-element {
            display: flex;
            align-items: center;
            font-weight: ${fontWeights.bolder};

            ${mediaQuery.until(breakpoints.medium)} {
                width: 100%;

                .checkbox-switch + label {
                    width: 100%;
                    padding-left: 0;

                    &:before {
                        left: auto;
                        right: 0;
                    }
                }
            }
        }
    }

    .mobile-modal-trigger {
        cursor: pointer;
        background: #fff;
        border: 1px solid ${colors.bark20};
        padding: 12px 12px 12px 8px;
        margin-bottom: ${gutterSizes.base};
        color: ${colors.bark};
        font-weight: ${fontWeights.bold};
        border-radius: 4px;
        width: 100%;

        .select-features {
            display: flex;
            justify-content: space-between;

            i {
                color: ${colors.bark60};
                font-style: unset;
                font-weight: 300;
            }
        }

        .mobile-preview-container {
            display: flex;
            align-items: center;

            .mobile-preview {
                font-size: 12px;
                font-weight: normal;
                color: ${colors.bark60};
                width: 110px;
                height: 20px;
                margin-right: ${gutterSizes.small};
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }
    }

    .back-button {
        position: absolute;
        top: ${gutterSizes.large};
        left: ${gutterSizes.large};
        z-index: 5;

        ${mediaQuery.from(breakpoints.medium)} {
            grid-row: 3;
            justify-self: flex-start;
            top: auto;
            left: auto;
            position: relative;
        }
    }

    .radio-switch {
        display: none;

        & + label {
            margin-bottom: ${gutterSizes.large18};
        }
    }

    .all-done-screen {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 65vh;

        .icon--check {
            font-size: 70px;
            color: ${colors.green};
        }

        .panel-title {
            font-size: 20px;
        }
    }

    ${mediaQuery.until(breakpoints.medium)} {
        .button {
            height: 65px;
            top: 0;
            right: 0;
            z-index: 6;
            position: fixed;
        }
    }
`;

export const vehicleSpecificationsCss = (theme) => css`
    &.panel {
        margin-bottom: ${gutterSizes.medium};
    }

    .vehicle-spec-validation {
        margin: ${gutterSizes.xlarge} 0 0;

        .txt-error {
            color: ${colors.red};
            margin-bottom: ${gutterSizes.medium};
        }

        a {
            color: ${theme.palette.action.active};

            &:hover {
                text-decoration: underline;
            }
        }

        .space-mts {
            margin-top: ${gutterSizes.medium};
        }
    }

    .dl-attribute-list {
        display: grid;
        grid-template-columns: 1fr 1fr;
        margin: 0;

        dd,
        dt {
            border-bottom: 1px solid ${colors.bark10};
            padding: 6px 0 3px;
            min-height: 38px;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: inherit;
            margin: 0;
        }

        dd {
            font-weight: 600;
        }
    }

    .vehicle-spec-summary {
        .grid-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-column-gap: 16px;

            ${mediaQuery.until(breakpoints.large)} {
                grid-template-columns: 1fr;
            }
        }
    }

    label[for='post-licence-plate'] {
        font-weight: 700;
    }

    .textbox-vrn-licence {
        display: flex;
        align-items: center;
        gap: ${gutterSizes.large};
        position: relative;
        margin: ${gutterSizes.base} 0 ${gutterSizes.large};

        .btn-primary {
            height: 44px;
            line-height: 40px;
            border-radius: ${boxSizes.borderRadius4};
            padding: 0 6px;
            font-size: ${fontSizes.medium};
            cursor: pointer;

            &.is-disabled {
                border: 1px solid ${theme.palette.disabled.border};
                background: ${theme.palette.disabled.main};
                pointer-events: none;
                color: ${theme.palette.disabled.mainContrastText};
            }
        }

        input {
            background: #fdec30;
            padding-left: 40px;
            text-transform: uppercase;
        }

        .vrn-gb-image {
            width: 33px;
            height: 44px;
            position: absolute;
            top: 0;
            left: 0;
            background-size: 100% 100%;
            background-repeat: no-repeat;
        }
    }

    button.button-link {
        padding: 0;
        margin: 0;
        color: ${theme.palette.action.active};

        &:hover {
            text-decoration: underline;
        }
    }

    .vrn-input-container {
        position: relative;
    }

    input[id='attributes[vehicle_mileage]'] {
        width: calc(50% - 8px);
        margin-top: ${gutterSizes.base};
    }

    .vrn-fields-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px 16px;

        .form-row {
            display: grid;
            gap: 8px;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            grid-template-columns: 1fr;
        }
    }

    ${mediaQuery.until(breakpoints.small)} {
        .vrn-input-container {
            width: 100%;
        }

        .textbox-vrn-licence {
            flex-direction: column;

            input {
                width: 100%;
            }

            .btn-primary {
                width: 100%;
                text-align: center;
            }
        }
    }
`;

export const petAdvicePanelCss = (theme) => css`
    &.info-box {
        .icon--paw {
            display: none;
        }

        &.has-border {
            margin-bottom: ${gutterSizes.xxxxlarge};

            &:after {
                bottom: -24px;
            }
        }
    }

    .icon--paw {
        font-size: ${fontSizes.pgLarge};
        padding-top: ${gutterSizes.small};
    }

    .toggle {
        display: inline;

        .content {
            display: inline;

            a {
                color: ${theme.palette.action.active};

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        .open {
            .content {
                display: inline;
            }
        }
    }

    .inline {
        .toggler {
            display: none;
        }
    }

    ${mediaQuery.until(breakpoints.small)} {
        .toggle {
            .content {
                display: none;
            }
        }

        .inline {
            .toggler {
                display: block;
                position: absolute;
                bottom: 0;
                right: 12px;

                h4,
                span {
                    color: #007fb0;
                }

                h4 {
                    margin-bottom: ${gutterSizes.base};
                }
            }
        }
    }
`;

export const PetsPanelCss = styled.div`
    label {
        display: block;
    }

    p {
        margin: ${gutterSizes.medium} 0;
    }

    .select-wrapper-small {
        width: calc(50% - ${gutterSizes.base});

        ${mediaQuery.until(breakpoints.medium)} {
            width: 100%;
        }
    }

    .pets-hint-text {
        margin: ${gutterSizes.small} 0 ${gutterSizes.medium};
    }

    .date-picker-container {
        display: block;

        input[type='date'] {
            width: 100%;
        }
    }

    .panel-content {
        & > div {
            &:not(:last-child) {
                margin-bottom: ${gutterSizes.large};
            }
        }
    }

    .hint-tooltip-trigger {
        margin: ${gutterSizes.small} 0 ${gutterSizes.medium} ${gutterSizes.small};
    }

    &.is-invalid .clear-error input[type='date'].is-invalid {
        border-color: ${colorVariables.borderLight};
    }
`;

export const PhonesPanelCss = styled.div`
    label {
        display: block;
    }

    p {
        margin: ${gutterSizes.medium} 0;
    }

    .panel-content {
        & > div {
            &:not(:last-child) {
                margin-bottom: ${gutterSizes.large};
            }
        }
    }
`;

export const PanelListCss = styled.div`
    label {
        display: block;
    }

    p {
        margin: ${gutterSizes.medium} 0;
    }

    .panel-content {
        & > div {
            &:not(:last-child) {
                margin-bottom: ${gutterSizes.large};
            }
        }
    }
`;

export const jobsAttributesCss = css`
    .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: ${gutterSizes.large};
        margin-bottom: ${gutterSizes.medium};

        .radio-switch {
            display: none;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            grid-template-columns: 1fr;
            gap: ${gutterSizes.medium} 0;
        }
    }

    .radio-button-container {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;

        ${mediaQuery.until(breakpoints.medium)} {
            grid-template-columns: 1fr 1fr;
            gap: ${gutterSizes.medium} 0;
        }
    }

    .textbox-prefix-pound {
        &:before {
            top: 11px;
        }

        input {
            padding-left: 22px;
        }
    }
`;

export const categoryChangeRadioCss = css`
    .category-change-radio {
        margin-right: ${gutterSizes.xlarge};

        ${mediaQuery.until(breakpoints.medium)} {
            display: block;
            margin-right: 0;
        }
    }
`;

export const CategoryChangePanel = styled.div`
    &.panel.category-change-panel {
        background: ${colors.green10};
        color: ${colors.green};
        display: flex;
        align-items: flex-start;
        gap: ${gutterSizes.xlarge};
        border-radius: ${gutterSizes.base};
        padding: ${gutterSizes.medium};
        margin-bottom: ${gutterSizes.xlarge};

        h4 {
            color: inherit;
            margin: 0;
            font-weight: ${fontWeights.bolder};
        }

        p {
            margin: 0;
        }

        .panel-validation {
            font-size: 26px;
            margin-top: ${gutterSizes.small};
            margin-left: 0 !important;
            width: 20px;
        }

        .panel-content {
            padding-bottom: 0;
        }

        ${mediaQuery.until(breakpoints.medium)} {
            gap: ${gutterSizes.large};

            .panel-validation {
                align-self: flex-start;
            }
        }
    }
`;

export const SellerSkillsSearchableFormRow = styled.div`
    width: 100%;
    padding: 0 ${gutterSizes.base} 0 0;

    ${mediaQuery.until(breakpoints.medium)} {
        width: 100%;
    }
`;

export const SellerSkillsWrapper = styled.div<{ hasData: boolean }>`
    hr {
        margin-top: ${gutterSizes.medium};
    }

    .seller-skills-form {
        width: 100%;
    }

    .panel-title-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .panel-title {
            margin-bottom: ${gutterSizes.base};
        }
    }

    ${mediaQuery.until(breakpoints.medium)} {
        .seller-skills-form {
            width: 100%;
        }
    }

    .skills-panel {
        min-height: ${gutterSizes.xxsmall};

        margin: ${gutterSizes.xlarge} 0 ${gutterSizes.xxxlarge} 0;

        &:after {
            content: '';
            position: absolute;
            bottom: -${gutterSizes.xlarge};
            left: -${gutterSizes.xlarge};
            display: ${({ hasData }) => (hasData ? 'block' : 'none')};
            height: ${gutterSizes.xxsmall};
            width: calc(100% + ${gutterSizes.xxxxlarge});
            background: ${colorVariables.borderLight};
        }
    }
`;

export const InfoBoxWrapper = styled.div`
    .info-box {
        margin-top: 6px !important;
        margin-bottom: 14px;
        .info-box-title {
            font-weight: 600;
            font-size: 15px;
            line-height: 22px;
            color: ${colors.bark};
        }
        .txt-btn {
            margin-top: 8px;
            text-decoration: underline;
            color: ${colors.navy};
            font-weight: ${fontWeights.bold};
        }
        .box-close-icon {
            top: 16px;
            right: 16px;
            bottom: unset;
        }
    }
`;
